import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { ThemeService } from './theme/theme.service';
import { ProjectType } from '../models/enums';
import { Engine } from '../engine/engine';
import { UnitConversionService } from '@infinitform/shared-ui';
import { withDefaultGraphics } from '@infinitform/platform-core';
import { hasViewableResults, displayStatusFor as displayStatus } from './viewable-results.util';
import { EmbeddedBridgeService } from './embedded/embedded-bridge.service';
import { CadCheckService } from '../features/flow/models/cad-check/cad-check.service';

/**
 * `Promise.allSettled` for a heterogeneous tuple, preserving each element's
 * type. `Promise.allSettled` itself widens a mixed array to a union, which
 * loses the per-result types the callers below need.
 */
function settle<T>(promise: Promise<T>): Promise<PromiseSettledResult<T>> {
  return promise.then(
    (value): PromiseSettledResult<T> => ({ status: 'fulfilled', value }),
    (reason): PromiseSettledResult<T> => ({ status: 'rejected', reason })
  );
}

export interface EngineInitConfig {
  canvas: HTMLCanvasElement;
  viewHelper: HTMLElement;
  css2dContainer: HTMLElement;
}

@Injectable({ providedIn: 'root' })
export class EngineInitializerService {
  private session = inject(SessionService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private unitConversionService = inject(UnitConversionService);
  private zone = inject(NgZone);
  private embeddedBridge = inject(EmbeddedBridgeService);
  private cadCheck = inject(CadCheckService);

  /**
   * A parametric-assembly download rejects with this whenever the
   * optimization has no prismatic result — the normal case for an organic-only
   * run, not a failure worth an error log.
   */
  private isExpectedRejection(name: string, reason: unknown): boolean {
    return name === 'parametricAssembly' && String(reason).includes('No additive_design_parametric URL available');
  }

  async initUserSettings(): Promise<void> {
    this.session.progressService.startPhase('user-settings', 'Loading user settings...');
    this.session.progressService.updateProgress(5, 'Loading user settings...');

    const settings: { mouse: unknown; graphics: unknown; keyboardShortcuts?: unknown } = (await this.session.getUserSettings()) as { mouse: unknown; graphics: unknown; keyboardShortcuts?: unknown };
    this.session.currentMousePreset = settings.mouse;
    this.session.currentGraphicsSettings = withDefaultGraphics(settings.graphics as object | null | undefined);
    if (settings.keyboardShortcuts) {
      this.session.keyboardShortcuts.importFromSync(settings.keyboardShortcuts);
    }

    this.session.progressService.updateProgress(10, 'User settings loaded');
    this.session.progressService.completeTask('user-settings', 'User settings loaded');
  }

  /**
   * Whether a project that is NOT in a normally-openable status (1 Ready,
   * 2 Error, 4 Done) nonetheless has something worth showing.
   *
   * A multi-manufacturing master's status is DERIVED from its methods, so it
   * stays in an in-progress status (Queued / optimizing) until EVERY method is
   * terminal — including while some methods are already Done and perfectly
   * viewable. That is the normal steady state of an HPC round-trip, where
   * results are imported one method at a time: 2 of 3 done, master still
   * Queued. Blocking those masters behind the "Optimization in Progress" card
   * makes finished methods unreachable until the last one lands.
   *
   * `runSummary.done` is authoritative here (the backend derives it from the
   * children). Everything downstream already copes: the pre-merge below picks
   * the first Done child, and the Results page renders only Done children and
   * marks the rest pending.
   */
  private hasViewableCase(project: { status: number }): boolean {
    return hasViewableResults(project);
  }

  /**
   * The status to hand `engine.init()`, which renders optimized geometry only
   * for status 4 (Done).
   *
   * A partially imported multi-manufacturing master sits at an in-progress
   * status until EVERY method lands, yet the finished methods' geometry is
   * already merged and perfectly renderable — so a 2-of-3 master would open
   * (hasViewableCase) and then show only the input part, which reads as "the
   * results didn't import". Present it as Done for display purposes; the
   * inner `organicAssemblyAvailable` / `parametricAssemblyAvailable` guards
   * still decide what actually gets drawn.
   */
  displayStatusFor(project: { status: number }): number {
    return displayStatus(project);
  }

  /**
   * True when this project is a multi-manufacturing master whose
   * `optimization` URL block still has to be filled in from a Done child.
   *
   * Result downloads cannot be started ahead of that merge, because the URLs
   * they read do not exist yet.
   */
  private needsChildPreMerge(): boolean {
    const project = this.session.project as {
      manufacturingMethods?: Array<{ methodId: string }>;
      childProjectIds?: string[];
      optimization?: { additive_design?: string };
    };
    return (
      (project.manufacturingMethods?.length ?? 0) >= 1 &&
      (project.childProjectIds?.length ?? 0) >= 1 &&
      !project.optimization?.additive_design
    );
  }

  /**
   * Starts the two large optimized-mesh downloads without awaiting them.
   *
   * Progress reporting is suppressed (`trackProgress: false`): these run while
   * the "Assembly Loading" phase owns the progress bar, and the phase model is
   * single-phase, so letting them post task updates here would make the bar
   * jump around and then be wiped by the `clearTasks()` that precedes the
   * optimization-data phase. They are reported as complete once that phase
   * starts, which is accurate — by then they usually are.
   */
  private startResultDownloads(): Promise<[PromiseSettledResult<ArrayBuffer>, PromiseSettledResult<ArrayBuffer>]> {
    return Promise.allSettled([
      this.session.getOptimizedAssembly(false),
      this.session.getOptimizedAssemblyParametric(false)
    ]) as Promise<[PromiseSettledResult<ArrayBuffer>, PromiseSettledResult<ArrayBuffer>]>;
  }

  async initializeEngine(config: EngineInitConfig, skipModelsLoading?: boolean): Promise<Engine | null> {
    try {
      // Start the project setup phase
      this.session.progressService.startPhase('project-setup', 'Initializing project...');

      console.log('[EngineInit] Starting initializeEngine...');
      const project = (await this.session.getProject()) as { status: number; proto: string; projectType: ProjectType; error?: string; code?: string; ai_analysis_pending?: boolean };

      console.log('[EngineInit] getProject result — status:', project?.status, '| projectType:', project?.projectType, '| proto:', project?.proto, '| has optimization:', !!(project as any)?.optimization);

      if (!project) {
        console.error('[EngineInit] No project returned — redirecting to flow-status');
        this.router.navigateByUrl('/flow-status', {
          state: { type: 'error' }
        });
        return null;
      } else if ('error' in project && 'code' in project) {
        console.error('[EngineInit] Project has error:', project.error, '| code:', project.code);
        this.router.navigateByUrl('/flow-status', {
          state: {
            title: 'Shared Resource',
            message: project.error,
            type: 'error'
          }
        });
        return null;
      } else if (![1, 2, 4].includes(project.status) && !this.hasViewableCase(project)) {
        if (project.projectType === ProjectType.SoftJaws) {
          // TODO: Handle soft jaws project status
        } else {
          const isFea = (project as any).config?.runType === 1;
          this.router.navigateByUrl('/flow-status', {
            state: {
              type: 'in-progress',
              title: isFea ? 'FEA in progress' : 'Optimization in progress',
              projectId: this.session.project_id,
            }
          });
        }
        return null;
      }

      // Multi-manufacturing-method: if the user navigated directly to a
      // CHILD project (via stale bookmark, admin link, or manual URL edit),
      // transparently redirect to the master. Children are never visible to
      // users per design #4 — the redirect rescues stale URLs without
      // breaking them.
      const masterProjectId = (project as { masterProjectId?: string | null }).masterProjectId;
      if (masterProjectId) {
        this.router.navigateByUrl(`/editor/${masterProjectId}`, { replaceUrl: true });
        return null;
      }

      this.session.setProject(project);
      // Problems found in the imported CAD, for the Models panel; never waited on.
      void this.cadCheck.load((project as { cadCheck?: string }).cadCheck);

      const engine = new Engine(
        config.canvas,
        config.viewHelper as HTMLDivElement,
        config.css2dContainer as HTMLDivElement,
        this.session,
        this.themeService,
        this.unitConversionService,
        this.zone
      );

      // Complete project setup phase and start assembly loading phase
      this.session.progressService.completePhase();
      this.session.progressService.startPhase('assembly-loading', 'Loading assembly...');

      engine.progressUpdate.subscribe(({ total, current }) => {
        this.session.assemblyInitProgress.total = total;
        this.session.assemblyInitProgress.current = current;

        // Only update progress if we're still in the assembly-loading phase
        if (this.session.progressService.getCurrentPhase() === 'Assembly Loading') {
          const progressRatio = total > 0 ? current / total : 0;
          const subProgress = 40 + progressRatio * 10;
          this.session.progressService.updateProgress(subProgress, `Initializing assembly... ${current}/${total}`);
        }
      });

      // Kick the optimized-result downloads off *now*, concurrently with the
      // base assembly, instead of waiting for it to finish first.
      //
      // Their URLs come from `project.optimization`, which `setProject` above
      // has already populated, so there is no reason to serialize two large
      // transfers. The one exception is a multi-manufacturing master, whose
      // `optimization` block is rewritten by the child pre-merge below — for
      // those we must still read the URLs after the merge, so we skip the
      // prefetch and fall back to the original ordering.
      const isTopologyOptimization = project.projectType === ProjectType.TopologyOptimization;
      const wantsResults =
        isTopologyOptimization &&
        !skipModelsLoading &&
        (project.status === 4 || !!(this.session.project as { runSummary?: { done?: number } }).runSummary?.done);
      const needsChildPreMerge = this.needsChildPreMerge();

      // Reset the loader-bar aggregate before ANY download starts. The
      // optimized meshes below are prefetched concurrently with the base
      // assembly, so no single download can own this reset.
      this.session.beginAssemblyDownloadBatch();

      const prefetchedResults =
        wantsResults && !needsChildPreMerge ? this.startResultDownloads() : null;

      console.log('[EngineInit] Loading assembly from proto:', project.proto);
      await engine.setAssembly(await this.session.getAssembly(project.proto));
      console.log('[EngineInit] Assembly loaded successfully');

      if (project.projectType === ProjectType.TopologyOptimization) {
        if (!skipModelsLoading) {
          // Multi-manufacturing-method master: the master has no results of
          // its own (by design — all results live under children). Pre-merge
          // the first Done child's `optimization` URL block into
          // `session.project.optimization` so the subsequent optimized-
          // assembly loading block reads valid URLs instead of empty ones.
          const cases = (this.session.project as { manufacturingMethods?: Array<{ methodId: string }> })
            .manufacturingMethods;
          const childIds = (this.session.project as { childProjectIds?: string[] })
            .childProjectIds;
          if (
            cases && cases.length >= 1 &&
            childIds && childIds.length >= 1 &&
            !this.session.project.optimization?.additive_design
          ) {
            console.log('[EngineInit] Multi-manufacturing master detected — pre-merging first Done child optimization...');
            try {
              const children = await this.session.fetchMasterChildren();
              const firstDone = (children as Array<{ id: string; status: number }>)
                .find((c) => c.status === 4);
              if (firstDone) {
                await this.session.mergeChildOptimization(firstDone.id);
                console.log('[EngineInit] Pre-merged child', firstDone.id, 'optimization into master');
              }
            } catch (err) {
              console.warn('[EngineInit] Multi-manufacturing pre-merge failed (continuing):', err);
            }
          }

          if (project.status === 4 || (this.session.project as { runSummary?: { done?: number } }).runSummary?.done) {
            console.log('[EngineInit] Status 4 / done children — loading optimization data...');
            console.log('[EngineInit] optimization.additive_design:', (this.session.project as any).optimization?.additive_design || '(empty)');
            console.log('[EngineInit] optimization.additive_design_parametric:', (this.session.project as any).optimization?.additive_design_parametric || '(empty)');
            console.log('[EngineInit] optimization.v2:', JSON.stringify((this.session.project as any).optimization?.v2)?.substring(0, 300));
            // Complete assembly loading phase and start optimization data phase
            this.session.progressService.completePhase();
            this.session.progressService.startPhase('optimization-data', 'Loading optimization data...');
            this.session.progressService.clearTasks();

            // Start parallel tasks with weights
            this.session.progressService.startTask('optimized-assembly', 30, 'Starting optimized assembly download...');
            this.session.progressService.startTask('optimization-info', 40, 'Starting optimization info loading…');
            this.session.progressService.startTask(
              'parametric-assembly',
              20,
              'Starting parametric assembly download...'
            );
            this.session.progressService.startTask('reaction-forces', 10, 'Starting reaction forces loading…');

            // The two mesh downloads may already be in flight (or finished)
            // from the prefetch above; only start them here if they were not.
            const meshDownloads = prefetchedResults ?? this.startResultDownloads();
            const [meshResults, infoResult, reactionResult] = await Promise.all([
              meshDownloads,
              settle(this.session.getOptimizedAssemblyInfo()),
              settle(this.session.getReactionForces())
            ]);

            if (prefetchedResults) {
              // Downloaded concurrently with the base assembly — reflect that
              // in the bar rather than leaving these tasks stuck at 0.
              this.session.progressService.completeTask('optimized-assembly', 'Optimized assembly downloaded');
              this.session.progressService.completeTask('parametric-assembly', 'Parametric assembly downloaded');
            }

            const [organicResult, parametricResult] = meshResults;

            console.log('[EngineInit] result loading outcomes:');
            for (const [name, r] of [
              ['optimizedAssembly', organicResult],
              ['optimizedInfo', infoResult],
              ['parametricAssembly', parametricResult],
              ['reactionForces', reactionResult]
            ] as const) {
              if (r.status === 'fulfilled') {
                console.log(`[EngineInit]   ${name}: fulfilled`);
              } else if (this.isExpectedRejection(name, r.reason)) {
                console.debug(`[EngineInit]   ${name}: REJECTED —`, r.reason);
              } else {
                console.error(`[EngineInit]   ${name}: REJECTED —`, r.reason);
              }
            }

            // Handle optimized assembly
            if (organicResult.status === 'fulfilled') {
              await engine.setOptimizedAssembly(organicResult.value);
              this.session.organicAssemblyAvailable = true;
            } else {
              this.session.organicAssemblyAvailable = false;
            }

            // Handle optimized info
            if (infoResult.status === 'fulfilled') {
              engine.setOptimizedInfo(infoResult.value);
              this.session.optimizedInfoAvailable = true;
            } else {
              this.session.optimizedInfoAvailable = false;
            }

            // Handle optimized parametric assembly
            if (parametricResult.status === 'fulfilled') {
              await engine.setOptimizedAssemblyParametric(parametricResult.value);
              this.session.parametricAssemblyAvailable = true;
            } else {
              this.session.parametricAssemblyAvailable = false;
            }
          }
        }
      }

      const displayStatus = this.displayStatusFor(project);
      console.log('[EngineInit] Calling engine.init with status:', displayStatus,
        displayStatus !== project.status ? `(project.status ${project.status} — partial master with viewable results)` : '');
      await engine.init(displayStatus);
      this.session.setEngine(engine);
      // A background editor in the cabinet finishes loading without drawing.
      if (this.embeddedBridge.hostPaused()) engine.pause();

      // Apply saved part visibility settings
      this.session.applyPartVisibility();

      // Apply saved lighting / ambient occlusion settings
      this.session.applyEngineGraphicsOptions();

      // Complete the optimization data phase and finish loading
      this.session.progressService.updateMessage('Project loaded successfully');
      this.session.progressService.completePhase();

      this.session.loadingProject = false;
      this.embeddedBridge.setLoading(false);
      console.log('[EngineInit] Engine initialization complete');

      return engine;
    } catch (err) {
      console.error('[EngineInit] FATAL ERROR — redirecting to flow-status:', err);
      this.router.navigateByUrl('/flow-status', {
        state: { type: 'error' }
      });
      return null;
    }
  }
}
